import React, { useContext, useEffect, useMemo, useState } from "react";
import { Grid, Typography, Button, Box, FormControlLabel, Checkbox, TextField } from "@mui/material";
import { ThemeContext } from "../../../context/ThemeContext";
import { removeCookies } from "../../../services/Cookies";
import { useDispatch } from "react-redux";
import { logout, setLoggedIn, setUserDetails, setDecimalPlaces } from '../../../store/Slices/userSlice'
import { useSelector } from "react-redux";
import { LoginApi } from '../../../api/request/auth'
import CryptoJS from "crypto-js";
import toast from "react-hot-toast";
import { setCookies } from "../../../services/Cookies";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MdOutlineKeyboardBackspace } from "react-icons/md";
import { addJournalLog } from "../../../components/tab/tabview/journal/addJournalLog";

const UserName = ({ back }) => {
    const [passwordDetails, setPasswordDetails] = useState({ oldPassword: "", newPassword: "", confirmPassword: "", });
    const [isChangePasswordVisible, setIsChangePasswordVisible] = useState(false);
    const { darkMode } = useContext(ThemeContext)
    const dispatch = useDispatch()
    const { userDetail } = useSelector((state) => state.user);
    const id = localStorage.getItem('clientId')
    const savedCredentials = useMemo(() => JSON.parse(localStorage.getItem("savedCredentials") || "{}"), []);
    const UserName = savedCredentials.username;
    const password = savedCredentials.password;
    const SECRET_KEY = process.env.REACT_APP_SECRET_KEY
    const { loggedIn } = useSelector(state => state.user);
    const { t } = useTranslation()
    const navigate = useNavigate();
    const accountID = localStorage.getItem('accountID')
    const Jusername = localStorage.getItem('username')

    const handleLogout = async () => {
        addJournalLog('logout', `Account ${Jusername}: ${accountID} is logged out`);

        setTimeout(() => {
            removeCookies("username", { path: "/" });
            removeCookies("token", { path: "/" });
            removeCookies("clientId", { path: "/" });
            removeCookies('gmtOffset', { path: '/' });

            localStorage.removeItem(`visibleColumns_${id}`)
            localStorage.setItem("accountID", "");
            localStorage.removeItem('clientId')
            dispatch(setLoggedIn(false));
            dispatch(logout());

            window.location.href = "/";
        }, 200);
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordDetails({ ...passwordDetails, [name]: value });
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        try {
            const encryptedData = savedCredentials.password;

            // Extract IV and encrypted part
            const iv = CryptoJS.enc.Base64.parse(encryptedData.substr(0, 24));
            const encryptedPassword = encryptedData.substring(24);

            const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
            const bytes = CryptoJS.AES.decrypt(encryptedPassword, key, { iv: iv });
            const decryptedPassword = bytes.toString(CryptoJS.enc.Utf8);

            // Compare decrypted password with input
            if (decryptedPassword !== passwordDetails.oldPassword) {
                toast.error("Old password is incorrect.");
                return;
            }

            if (passwordDetails.newPassword !== passwordDetails.confirmPassword) {
                toast.error("New password and confirm password do not match!");
                return;
            }

            // Encrypt new password
            const newIv = CryptoJS.lib.WordArray.random(16);
            const newEncrypted = CryptoJS.AES.encrypt(passwordDetails.newPassword, key, {
                iv: newIv,
            });
            const newEncryptedPassword = CryptoJS.enc.Base64.stringify(newIv) + newEncrypted.toString();

            const updatedCredentials = {
                ...savedCredentials,
                password: newEncryptedPassword,
            };

            localStorage.setItem("savedCredentials", JSON.stringify(updatedCredentials));
            toast.success("Password changed and encrypted successfully!");
            setIsChangePasswordVisible(false);
        } catch (err) {
            console.error("Password change error:", err);
            toast.error("Something went wrong while verifying your old password.");
        }
    };

    const handleLoginSaved = async (e) => {
        e.preventDefault();

        const savedData = localStorage.getItem("savedCredentials");
        if (!savedData) {
            toast.error("No saved login credentials found.");
            return;
        }

        try {
            const { username, password: encryptedData } = JSON.parse(savedData);

            const iv = CryptoJS.enc.Base64.parse(encryptedData.substr(0, 24));
            const encryptedPassword = encryptedData.substring(24);

            const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
            const bytes = CryptoJS.AES.decrypt(encryptedPassword, key, { iv: iv });
            const decryptedPassword = bytes.toString(CryptoJS.enc.Utf8);

            if (!decryptedPassword) {
                toast.error("Failed to decrypt saved password.");
                return;
            }

            const params = {
                login_type: "api",
                username,
                password: decryptedPassword,
            };

            const { data } = await LoginApi(params);

            if (data?.token) {
                // Set current logged-in user
                localStorage.setItem("username", username);
                localStorage.setItem("clientId", data?.client?.id);
                toast.success("Login successful!");
                setCookies("username", username, { path: "/", secure: true, sameSite: "strict" });
                setCookies("token", data?.token, { path: "/", secure: true, sameSite: "strict" });
                setCookies("refreshToken", data?.refreshToken, { path: "/", secure: true, sameSite: "strict" });
                setCookies("clientId", data?.client?.id, { path: "/", secure: true, sameSite: "strict" });
                setCookies("gmtOffset", data?.gmtOffset, { path: "/", secure: true, sameSite: "strict" });
                // addJournalLog('login', `Account ${username}: ${accountId} is logged in successfully.`);

                dispatch(setLoggedIn(true));
                dispatch(setUserDetails(data?.client));
                dispatch(setDecimalPlaces(data?.decimalPlacesForAmountFormatting));
                navigate("/dashboard");
            } else {
                toast.error("Login failed. Please check your credentials.");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "An error occurred while logging in.";
            toast.error(errorMessage);
        }
    };

    const handleRemove = () => {
        localStorage.removeItem("savedCredentials");
        localStorage.removeItem("clientId")
        toast.success("Saved credentials removed.");
    };

    useEffect(() => {
        if (isChangePasswordVisible) {
            const savedData = localStorage.getItem("savedCredentials");
            if (savedData) {
                try {
                    const { password: encryptedData } = JSON.parse(savedData);

                    const iv = CryptoJS.enc.Base64.parse(encryptedData.substr(0, 24));
                    const encryptedPassword = encryptedData.substring(24);

                    const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
                    const bytes = CryptoJS.AES.decrypt(encryptedPassword, key, { iv: iv });
                    const decryptedPassword = bytes.toString(CryptoJS.enc.Utf8);
                    console.log(decryptedPassword);

                    setPasswordDetails(prev => ({
                        ...prev,
                        oldPassword: decryptedPassword
                    }));
                } catch (err) {
                    toast.error("Failed to prefill password.");
                }
            }
        }
    }, [isChangePasswordVisible]);

    return (
        <>
            {!isChangePasswordVisible ? (
                <Grid container alignItems="center" spacing={2} sx={{ padding: 0 }} >
                    <Grid item xs={1}><MdOutlineKeyboardBackspace onClick={back} style={{ fontSize: '1.5rem', color: 'var(--primary)', cursor: 'pointer' }} /></Grid>
                    <Grid item xs={11}><Typography variant="caption" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }} >{id || "N/A"} - {process.env.REACT_APP_COMPANY_NAME} -Demo</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{t('name')}</Typography></Grid>
                    <Grid item xs={9}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{UserName} - {id}</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{t('deposit')}</Typography></Grid>
                    <Grid item xs={9}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}> {userDetail?.deposit || '100000.00'}</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{t('server')}</Typography></Grid>
                    <Grid item xs={9}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{process.env.REACT_APP_COMPANY_NAME} - Demo</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}> {t('password')}</Typography></Grid>
                    <Grid item xs={3}><Typography textAlign='left' variant="body2" sx={{ color: "green" }}> {password ? "Saved" : "Not Saved"}</Typography></Grid>
                    <Grid item xs={6}><Typography textAlign='left' variant="body2" className="text-primary" onClick={() => setIsChangePasswordVisible(true)} sx={{ cursor: "pointer" }}>{t('changePassword')}</Typography></Grid>
                    <Grid container spacing={1} px={2}>
                        <Grid item xs={8}><Button fullWidth type="submit" onClick={handleLoginSaved} variant="contained" color="secondary" sx={{ border: "1px solid green", marginTop: "20px", color: "white", padding: "5px", pl: 2, pr: 2, textTransform: "none", }}>{t('connectButton')}</Button></Grid>
                        <Grid item xs={4}><Button fullWidth onClick={loggedIn ? handleLogout : handleRemove} type="submit" variant="outlined" sx={{ border: "1px solid red", marginTop: "20px", color: "red", padding: "5px", pl: 2, pr: 2, textTransform: "none", }}>   {loggedIn ? `${t('logout')}` : `${t('remove')}`}</Button></Grid>
                    </Grid>
                </Grid>

            ) : null}
            {isChangePasswordVisible && (
                <Grid container spacing={1.5} sx={{ padding: 0 }} alignItems="center">
                    <Grid item xs={12}><Typography variant="h5" fontWeight={600} sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('changePassword')}</Typography></Grid>
                    <Grid item xs={12}><Typography variant="caption" color="textDisabled" sx={{ lineHeight: 1.5, m: 0, p: 0, display: 'block', whiteSpace: 'normal', }} >{t('disclaimer')} </Typography></Grid>
                    <Grid item xs={5}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}>{t('login')}</Typography></Grid>
                    <Grid item xs={7}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : "var(--black)" }}> {id}</Typography></Grid>
                    <Grid item xs={5}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('currentPassword')}</Typography></Grid>
                    <Grid item xs={7}><TextField name="oldPassword" type="password" placeholder={t('currentPassword')} fullWidth size="small" required value={passwordDetails.oldPassword} onChange={handlePasswordChange} className="custom-textfield" /></Grid>
                    <Grid item xs={5}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('newPassword')}</Typography></Grid>
                    <Grid item xs={7}><TextField type="password" placeholder={t('newPassword')} fullWidth size="small" required name="newPassword" value={passwordDetails.newPassword} onChange={handlePasswordChange} className="custom-textfield" /></Grid>
                    <Grid item xs={5}><Typography textAlign='left' variant="body2" sx={{ color: darkMode ? "var(--lightText)" : 'var(--black)' }}>{t('confirmPassword')}</Typography></Grid>
                    <Grid item xs={7}><TextField name="confirmPassword" type="password" placeholder={t('confirmPassword')} fullWidth size="small" required value={passwordDetails.confirmPassword} onChange={handlePasswordChange} className="custom-textfield" /></Grid>
                    <Grid container spacing={1} px={1.5} mt={1}>
                        <Grid item xs={8}><Button fullWidth type="submit" variant="contained" color="secondary" onClick={handlePasswordSubmit} sx={{ border: "1px solid green", color: "white", padding: "5px", textTransform: "none", }}>{t('changePassword')}</Button></Grid>
                        <Grid item xs={4}><Button fullWidth variant="outlined" color="secondary" onClick={() => setIsChangePasswordVisible(false)} sx={{ border: "1px solid green", textTransform: "none", padding: "5px", }} > {t('cancel')} </Button></Grid>
                    </Grid>
                </Grid>
            )}

        </>
    );
};

export default UserName;